import { Severity, addBreadcrumb, captureException } from '@sentry/browser';
import { AxiosInstance } from 'axios';
import { ReferencedEntry } from 'contentful';
import { SalesChannel } from 'src/api/contentful/contentTypes.shop';
import { cookieNames } from 'src/constants/cookieNames';
import { AGENT_ID_COOKIE_NAME, fetchContentfulCookieConfig, makeMiscInfoJsonString } from 'src/services/analytics/analytics';
import { updateAnalyticsId } from 'src/services/analytics/heap';
import { readCookie } from 'src/utils/Cookies';
import { getField, getSys } from 'src/utils/EntryUtil';
import { Environment } from 'src/utils/Environment';
import { getReferralType } from '../SalesChannel/Util';
import { FormValues } from './ContactFormTypes';

export enum FormEndpointType {
  LEADS = 'Leads',
  PURCHASE_ACCOUNT_ONLY = 'Purchase_Account_Only',
  PURCHASE_AB_TEST_ACCOUNT_ONLY = 'Purchase_Ab_Test_Account_Only',
  SBA_BOARDING = 'SBA_Boarding',
  SBA_BOARDING_PARTNER = 'SBA_Boarding_Partner',
  CONNECT_TO_SALES = 'Connect_To_Sales',
  PURCHASE_CLOVER_ESSENTIALS = 'Purchase_Clover_Essentials',
}

export const acquiringFormNameMap = {
  [FormEndpointType.LEADS]: 'Get Started',
  [FormEndpointType.PURCHASE_ACCOUNT_ONLY]: 'Account Only',
  [FormEndpointType.PURCHASE_AB_TEST_ACCOUNT_ONLY]: 'Account Only',
  [FormEndpointType.SBA_BOARDING]: 'SBA Boarding',
  [FormEndpointType.SBA_BOARDING_PARTNER]: 'SBA Boarding Partner',
  [FormEndpointType.CONNECT_TO_SALES]: 'Demo Request',
  [FormEndpointType.PURCHASE_CLOVER_ESSENTIALS]: 'Clover Essentials',
};

// Target Endpoint URLs
export const CART_ACCOUNTS_URL = '/v3/cart_accounts';
export const COS_LEADS_URL = '/v3/shop/leads';
export const PURCHASE_URL = '/v3/purchase';
export const SBA_BOARDING_URL = '/v3/sba_boarding';
export const GROWTH_LEADS_URL = '/growth/shop/v2/leads';

// Subscription Plans Contentful Entry IDs for no-hardware form submission
export const ACCOUNT_ONLY_PLAN_ENTRY_ID = Environment.current.isCloverProdEnvironment ? '5v0ql4ObWGuiKgnjHxsKCS' : '7suFJIizcwP3XNhPFcmnMn';
export const AB_TEST_ACCOUNT_ONLY_PLAN_ENTRY_ID = Environment.current.isCloverProdEnvironment ? '4yGVUMry22aqRQCs06I2f' : '31gWAhLj6NkqPQZC2lXadk';

// Subscription Plan Clover IDs for no-hardware form submission
export const CLOVER_ESSENTIALS_PLAN_ID = Environment.current.isCloverProdEnvironment ? 'KZ367QJCVE0CE' : 'D3X71BRK8V4ZE';

// Salesforce Owner Queues
export const CONNECT_TO_SALES_SALESFORCE_OWNER_ID = Environment.current.isCloverProdEnvironment ? '00G0g000004YKy4' : '00G0g000004YKy4'; // CBS Prequal Queue

// Sales Channel Contentful Entry IDs
const DEV_CLOVER_DIRECT_SALES_CHANNEL_ENTRY_ID = '1ziCtiMbFSsowi4qEaAgMu';
const PROD_CLOVER_DIRECT_SALES_CHANNEL_ENTRY_ID = '4T1FBCT2dOiQE8eMCQWkmu';

// Sales Channel Clover IDs
const DEV_CLOVER_DIRECT_SALES_CHANNEL_ID = '47HMNNZ9BPF8E';
const PROD_CLOVER_DIRECT_SALES_CHANNEL_ID = 'BVG1JKB6RM0DM';

// Order Template UUIDs
const NO_HARDWARE_ESSENTIALS_ORDER_TEMPLATE_CODE = 'NO_HARDWARE_ESSENTIALS';

const getResellerUuid = (salesChannel: ReferencedEntry<SalesChannel>) => {
  let salesChannelId = getField(salesChannel, 'cloverResellerId');
  if (!salesChannelId) {
    salesChannelId = Environment.current.isCloverProdEnvironment
        ? PROD_CLOVER_DIRECT_SALES_CHANNEL_ID
        : DEV_CLOVER_DIRECT_SALES_CHANNEL_ID;
  }
  return salesChannelId;
};

function getSalesChannelEntryId(salesChannel: ReferencedEntry<SalesChannel>) {
  let salesChannelEntryId = getSys(salesChannel, 'id');
  if (salesChannelEntryId === undefined) {
    salesChannelEntryId = Environment.current.isCloverProdEnvironment ? PROD_CLOVER_DIRECT_SALES_CHANNEL_ENTRY_ID
      : DEV_CLOVER_DIRECT_SALES_CHANNEL_ENTRY_ID;
  }
  return salesChannelEntryId;
}

async function getEmailAndUpdateAnalyticsId(values: FormValues) {
  const email = values.emailAddress && values.emailAddress.trim();
  await updateAnalyticsId(email);
  return email;
}

export const leadsEndpoint = (
  onSuccess: () => void,
  onError: () => void,
  apiClient: AxiosInstance,
  salesChannel: ReferencedEntry<SalesChannel>
) => {
  return async (values: FormValues, actions: any) => {
    const email = await getEmailAndUpdateAnalyticsId(values);
    const cookieConfig = await fetchContentfulCookieConfig();

    const miscInfoString = makeMiscInfoJsonString(cookieConfig, {
      businessName: values.businessName,
      contactPreference: values.phoneNumber,
      businessType: values.businessType,
      yearsInBusiness: values.yearsInBusiness,
      acquiring_form: acquiringFormNameMap[FormEndpointType.LEADS],
      leadSource: 'Value Digital or Digital Web',
    });

    const salesChannelEntryId = getSalesChannelEntryId(salesChannel);

    const apiRequestPromise = () => apiClient.post(COS_LEADS_URL, {
      cartProducts: [],
      email,
      firstName: values.firstName,
      lastName: values.lastName,
      miscInfo: miscInfoString,
      chainAgent: readCookie(AGENT_ID_COOKIE_NAME),
      shippingAddress: { phoneNumber: values.phoneNumber, zipCode: values.zipCode },
      salesChannelEntryId,
    });

    await submitToEndpointAndSendAnalyticsEvents(apiRequestPromise, onSuccess, onError, acquiringFormNameMap[FormEndpointType.LEADS], email);
    actions.setSubmitting(false);
  };
};

async function submitToEndpointAndSendAnalyticsEvents(apiRequestPromise: any, onSuccess: () => void, onError: () => void, formName: string, email?: string, eventTypes = ['form_submit']) {
  let responseStatus: number;
  const successfulApiResponse: any = await apiRequestPromise().catch((error: any) => {
    responseStatus = error.response && error.response.status;
    onError();
  });

  if (successfulApiResponse) {
    eventTypes.forEach(eventType => {
      sendGTMSubmissionEvent(
        formName,
        eventType,
        email
      );
    });
    responseStatus = successfulApiResponse.status;
    onSuccess();
  }

  eventTypes.forEach(eventType => {
    sendTealiumSubmissionEvent(
      formName,
      eventType,
      responseStatus,
      email
    );
  });
}

async function handleReferralResponse(values: FormValues, salesChannel: ReferencedEntry<SalesChannel> | undefined) {
  const apiResponse: any = await updateReferralLead(values, salesChannel);
  if (!apiResponse) return;
  const apiStatus = apiResponse.status || apiResponse.response.status;
  const shouldIncludeSalesforceId = apiStatus !== 422;
  if (apiStatus === 500 || apiStatus === 400) {
    addBreadcrumb({
      category: 'Referral Information Update',
      message: 'Error response while updating referral information',
      data: apiResponse,
      level: Severity.Error,
    });
    captureException(apiResponse);
  }
  return shouldIncludeSalesforceId;
}

async function updateReferralLead(values: FormValues, salesChannel: ReferencedEntry<SalesChannel> | undefined) {
  const growthApiClient = Environment.current.growthApiClient;
  return new Promise((resolve: any) => {
    growthApiClient.put(`/growth/partner/v1/referral`, {
      shopIntegrationToken: readCookie(cookieNames.INTEGRATION_TOKEN),
      salesforceId: readCookie(cookieNames.SALESFORCE_ID),
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.emailAddress,
      business: {
        name: values.businessName,
        phoneNumber: values.phoneNumber,
        address: values.businessAddressLine1 ? {
          lineOne: values.businessAddressLine1,
          lineTwo: values.addressLine2,
          city: values.city,
          state: values.state,
          zipCode: values.zipCode,
        } : undefined,
      },
      resellerUuid: getField(salesChannel, 'cloverResellerId'),
    }).then(response => {
      resolve(response);
    }).catch(err => {
      resolve(err);
    });
  });
}

export const connectToSalesEndpoint = (
  onSuccess: () => void,
  onError: () => void,
  apiClient: AxiosInstance,
  salesChannel: ReferencedEntry<SalesChannel>
) => {
  return async (values: FormValues, actions: any) => {
    const email = await getEmailAndUpdateAnalyticsId(values);
    const cookieConfig = await fetchContentfulCookieConfig();

    const marketingInfo = JSON.parse(makeMiscInfoJsonString(cookieConfig, {
      industry: values.businessType,
      yearsInBusiness: values.yearsInBusiness,
      zipCode: values.zipCode,
      acquiring_form: acquiringFormNameMap[FormEndpointType.CONNECT_TO_SALES],
      leadSource: 'Webform',
      bestCallTime: values.whenToCall,
    }));

    const apiRequestPromise = () => apiClient.put(GROWTH_LEADS_URL, {
      email,
      firstName: values.firstName,
      lastName: values.lastName,
      businessName: values.businessName,
      phoneNumber: values.phoneNumber,
      marketingInfo,
      chainAgentId: readCookie(AGENT_ID_COOKIE_NAME),
      shippingAddress: { zipCode: values.zipCode },
      resellerUuid: getField(salesChannel, 'cloverResellerId'),
      salesforceOwnerId: values.salesforceOwnerId ? values.salesforceOwnerId : CONNECT_TO_SALES_SALESFORCE_OWNER_ID,
      annualProcessingVolume: values.annualProcessingVolume,
      activityType: 'formSubmit_c',
    });
    await submitToEndpointAndSendAnalyticsEvents(apiRequestPromise, onSuccess, onError, acquiringFormNameMap[FormEndpointType.CONNECT_TO_SALES], email, ['form_submit', 'status']);
    actions.setSubmitting(false);
  };
};

export const purchaseAccountOnlyEndpoint = (
  onSuccess: () => void,
  onError: () => void,
  apiClient: AxiosInstance,
  salesChannel: ReferencedEntry<SalesChannel>
) => {
  return noHardwarePurchaseWithPlanEndpoint(onSuccess, onError, apiClient, salesChannel, ACCOUNT_ONLY_PLAN_ENTRY_ID, FormEndpointType.PURCHASE_ACCOUNT_ONLY);
};

export const abTestPurchaseAccountOnlyEndpoint = (
  onSuccess: () => void,
  onError: () => void,
  apiClient: AxiosInstance,
  salesChannel: ReferencedEntry<SalesChannel>
) => {
  return noHardwarePurchaseWithPlanEndpoint(onSuccess, onError, apiClient, salesChannel, AB_TEST_ACCOUNT_ONLY_PLAN_ENTRY_ID, FormEndpointType.PURCHASE_ACCOUNT_ONLY);
};

export const cloverEssentialsNoHardwareEndpoint = (
    onSuccess: () => void,
    onError: () => void,
    apiClient: AxiosInstance,
    salesChannel: ReferencedEntry<SalesChannel>
) => {
  const resellerUuid = getResellerUuid(salesChannel);
  return createGrowthLeadWithOrderEndpoint(
      onSuccess,
      onError,
      apiClient,
      FormEndpointType.PURCHASE_CLOVER_ESSENTIALS,
      resellerUuid,
      NO_HARDWARE_ESSENTIALS_ORDER_TEMPLATE_CODE
  );
};

/**
 * Submits a Growth API lead.
 * @param onSuccess A success function to call if the request succeeds
 * @param onError An error function to call if the request fails
 * @param apiClient The AxiosInstance which will make the request
 * @param formEndpointType The Form Endpoint Type to use
 * @param resellerUuid The Clover Reseller UUID
 * @param orderTemplateCode An optional order template code to submit the form with
 */
export const createGrowthLeadWithOrderEndpoint = (
    onSuccess: () => void,
    onError: () => void,
    apiClient: AxiosInstance,
    formEndpointType: FormEndpointType,
    resellerUuid: string,
    orderTemplateCode?: string
) => {
  return async (values: FormValues, actions: any) => {
    const cosApiClient = Environment.current.cosApiClient;
    const email = await getEmailAndUpdateAnalyticsId(values);
    const cookieConfig = await fetchContentfulCookieConfig();

    const marketingInfo = JSON.parse(makeMiscInfoJsonString(cookieConfig, {
      industry: values.businessType,
      yearsInBusiness: values.yearsInBusiness,
      zipCode: values.zipCode,
      acquiring_form: acquiringFormNameMap[formEndpointType],
      leadSource: 'Webform',
      bestCallTime: values.whenToCall,
    }));

    const apiRequestPromise = async () => {
      const createdLead = await apiClient.put(GROWTH_LEADS_URL, {
        email,
        firstName: values.firstName,
        lastName: values.lastName,
        businessName: values.businessName,
        phoneNumber: values.phoneNumber,
        marketingInfo,
        chainAgentId: readCookie(AGENT_ID_COOKIE_NAME),
        shippingAddress: { zipCode: values.zipCode },
        resellerUuid,
        salesforceOwnerId: values.salesforceOwnerId,
        annualProcessingVolume: values.annualProcessingVolume,
        activityType: 'formSubmit_c',
        orderTemplateCode,
      });
      return cosApiClient.post(CART_ACCOUNTS_URL, {
        id: createdLead.data.uuid,
      });
    };

    await submitToEndpointAndSendAnalyticsEvents(apiRequestPromise, onSuccess, onError, acquiringFormNameMap[formEndpointType], email, ['form_submit', 'status']);
    actions.setSubmitting(false);
  };
};

export const noHardwarePurchaseWithPlanEndpoint = (
  onSuccess: () => void,
  onError: () => void,
  apiClient: AxiosInstance,
  salesChannel: ReferencedEntry<SalesChannel>,
  planEntryId: string,
  formEndpointType: FormEndpointType
) => {
  return async (values: FormValues, actions: any) => {
    const email = await getEmailAndUpdateAnalyticsId(values);
    const cookieConfig = await fetchContentfulCookieConfig();

    const miscInfoString = makeMiscInfoJsonString(cookieConfig, {
      businessName: values.businessName,
      contactPreference: values.phoneNumber,
      businessType: values.businessType,
      yearsInBusiness: values.yearsInBusiness,
      acquiring_form: acquiringFormNameMap[formEndpointType],
      leadSource: 'Online Checkout',
    });

    const salesChannelEntryId = getSalesChannelEntryId(salesChannel);
    const isBCReferralFlow = getReferralType() === 'BC';
    let shouldIncludeSalesforceId: boolean | undefined = false;
    if (isBCReferralFlow) {
      shouldIncludeSalesforceId = await handleReferralResponse(values, salesChannel);
    }

    const apiRequestPromise = () => apiClient.post(PURCHASE_URL, {
      shopIntegrationToken: (isBCReferralFlow && shouldIncludeSalesforceId) ? readCookie(cookieNames.INTEGRATION_TOKEN) : undefined,
      salesforceId: shouldIncludeSalesforceId ? readCookie(cookieNames.SALESFORCE_ID) : undefined,
      referralType: isBCReferralFlow ? readCookie(cookieNames.REFERRAL_TYPE) : undefined,
      isCloverAccountOnly: true,
      email,
      firstName: values.firstName,
      lastName: values.lastName,
      miscInfo: miscInfoString,
      chainAgent: readCookie(AGENT_ID_COOKIE_NAME),
      shippingAddress: { phoneNumber: values.phoneNumber, zipCode: values.zipCode },
      salesChannelEntryId,
      planEntryId,
    }).then(createdLead => apiClient.post(CART_ACCOUNTS_URL, {
        id: createdLead.data.id,
      })
    );
    await submitToEndpointAndSendAnalyticsEvents(apiRequestPromise, onSuccess, onError, acquiringFormNameMap[formEndpointType], email);
    actions.setSubmitting(false);
  };
};

export const sbaBoardingEndpoint = (
  onSuccess: () => void,
  onError: () => void,
  apiClient: AxiosInstance,
  salesChannel: ReferencedEntry<SalesChannel>
) => {
  return async (values: FormValues, actions: any) => {
    const email = await getEmailAndUpdateAnalyticsId(values);

    const payload = {
      type: 'FISERV',
      planId: 'C3ZVS9RMPR1NJ',
      firstName: values.firstName,
      lastName: values.lastName,
      email,
      phoneNumber: values.mobilePhoneNumber,
      mobilePhoneNumber: values.mobilePhoneNumber,
      mid: values.mid,
      last4SSN: values.ssnLast4,
      last4DDA: values.bankAccountLast4,
    };

    const url = `${SBA_BOARDING_URL}?recaptcha_response=${values.googleRecaptcha}`;

    const apiRequestPromise = () => apiClient.post(url, payload);
    await submitToEndpointAndSendAnalyticsEvents(apiRequestPromise, onSuccess, onError, acquiringFormNameMap[FormEndpointType.SBA_BOARDING], email);
    actions.setSubmitting(false);
  };
};

export const sbaBoardingPartnerEndpoint = (
  onSuccess: () => void,
  onError: () => void,
  apiClient: AxiosInstance,
  salesChannel: ReferencedEntry<SalesChannel>
) => {
  return async (values: FormValues, actions: any) => {
    const email = await getEmailAndUpdateAnalyticsId(values);

    const payload = {
      type: 'PARTNER',
      planId: '5J013ZJ5W5AHA',
      resellerId: getField(salesChannel, 'nonProcessingResellerId'),
      firstName: values.firstName,
      lastName: values.lastName,
      email,
      phoneNumber: values.mobilePhoneNumber,
      mobilePhoneNumber: values.mobilePhoneNumber,
      businessName: values.businessName,
      address: {
        address1: values.businessAddressLine1,
        address2: values.addressLine2,
        city: values.city,
        state: values.state,
        zip: values.zipCode,
        country: 'US',
      },
    };

    const url = `${SBA_BOARDING_URL}?recaptcha_response=${values.googleRecaptcha}`;

    const apiRequestPromise = () => apiClient.post(url, payload);
    await submitToEndpointAndSendAnalyticsEvents(apiRequestPromise, onSuccess, onError, acquiringFormNameMap[FormEndpointType.SBA_BOARDING_PARTNER], email);
    actions.setSubmitting(false);
  };
};

export const FormEndpointMapper = {
  [FormEndpointType.LEADS]: leadsEndpoint,
  [FormEndpointType.PURCHASE_ACCOUNT_ONLY]: purchaseAccountOnlyEndpoint,
  [FormEndpointType.PURCHASE_AB_TEST_ACCOUNT_ONLY]: abTestPurchaseAccountOnlyEndpoint,
  [FormEndpointType.SBA_BOARDING]: sbaBoardingEndpoint,
  [FormEndpointType.SBA_BOARDING_PARTNER]: sbaBoardingPartnerEndpoint,
  [FormEndpointType.CONNECT_TO_SALES]: connectToSalesEndpoint,
  [FormEndpointType.PURCHASE_CLOVER_ESSENTIALS]: cloverEssentialsNoHardwareEndpoint,
};

export const ApiClientMapper = {
  [FormEndpointType.LEADS]: Environment.current.cosApiClient,
  [FormEndpointType.PURCHASE_ACCOUNT_ONLY]: Environment.current.cosApiClient,
  [FormEndpointType.PURCHASE_AB_TEST_ACCOUNT_ONLY]: Environment.current.cosApiClient,
  [FormEndpointType.SBA_BOARDING]: Environment.current.cosApiClient,
  [FormEndpointType.SBA_BOARDING_PARTNER]: Environment.current.cosApiClient,
  [FormEndpointType.CONNECT_TO_SALES]: Environment.current.growthApiClient,
  [FormEndpointType.PURCHASE_CLOVER_ESSENTIALS]: Environment.current.cosApiClient,
};

export function sendGTMSubmissionEvent(formType: string, eventType: string, email?: string) {
  const emailDomain = email && email.split('@')[1];
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: eventType,
    name: formType,
    heap_id: readCookie('clAnalyticsId'),
    domain: emailDomain,
  });
}

export function sendTealiumSubmissionEvent(formType: string, eventType: string, statusCode: number, email?: string) {
  const emailDomain = email && email.split('@')[1];
  if (!utag?.link) return;
  utag.link({
    event: eventType,
    name: formType,
    clAnalyticsId: readCookie('clAnalyticsId'),
    submitter_email_domain: emailDomain,
    response_status_code: statusCode,
  });
}
